/**
 * What `?error=` means, in one place (LGN-126).
 *
 * The platform ends several server-side paths with a redirect carrying a
 * reason code: the sign-in callback uses `denied`, `no_email`, `suspended` and
 * `stale_handshake`, and the re-authentication flow uses the three `reauth_*`
 * codes. Two different screens have to read them, because two different people
 * arrive: somebody signed out lands on the sign-in screen, and somebody who is
 * still signed in lands back in the shell with a confirmation that did not
 * finish. One table, so the two cannot drift.
 *
 * The table used to live in `sign-in.tsx` and fall back to the `denied`
 * sentence, which meant every code it did not know, including the three
 * `reauth_*` ones the platform was already sending, was reported as "that
 * account cannot sign in here": a more alarming claim than the truth and one
 * this code has no evidence for. Unknown codes now get `NEUTRAL`.
 *
 * None of these sentences says whether an account exists, whether an address is
 * allowlisted, or which organisation was involved. Somebody who has just failed
 * to sign in must not learn who does have access, and that silence is the same
 * one the platform's own refusal pages keep. The two sentences that are more
 * specific than that are specific about facts the browser already holds: which
 * link it followed, and, for `reauth_forbidden`, the rule `/auth/reauth`
 * already states in full to the same person.
 */

const REASONS: Record<string, string> = {
  denied: "That Black Pearl account cannot sign in here. Ask Tech Ops for access.",
  no_email: "That account has no email address, so it cannot be matched to an owner. Ask Tech Ops.",
  suspended: "This account is suspended. Ask Tech Ops if that is a mistake.",
  // The handshake behind the callback was gone, or the code had been spent.
  // Nothing was refused, so the sentence must not imply anything was.
  stale_handshake:
    "That sign-in link is no longer valid. It may have been left open too long, or already used. Try again.",
  reauth_expired:
    "That confirmation could not be completed because the session it belonged to had gone. Sign in again and retry the action.",
  reauth_mismatch:
    "That confirmation was completed by a different account than the one signed in here. Retry the action.",
  reauth_forbidden: "Only an owner can confirm that action.",
};

/**
 * The fallback.
 *
 * Deliberately says nothing about accounts or access: it is reached only when
 * a code arrives that this bundle does not recognise, which means a newer
 * platform is deployed in front of an older dashboard, and inventing a cause
 * for it is how a deploy-order mismatch becomes a support ticket about
 * permissions.
 */
const NEUTRAL = "That request could not be completed. Try again.";

/** The sentence for a reason code, or `null` when there is no code at all. */
export function authErrorMessage(reason: string | null): string | null {
  if (reason === null) return null;
  return REASONS[reason] ?? NEUTRAL;
}

/**
 * The reason code in a query string, if there is one.
 *
 * Takes the search string rather than reading `window` itself, so both callers
 * pass what they have and the function stays testable without a DOM.
 */
export function readAuthError(search: string): string | null {
  return new URLSearchParams(search).get("error");
}
